/***
 *
 *   help
 *   Pure helpers for the embedded help centre — no React, no side effects.
 *   Owns the article map lookup, slug validation, embed URL construction and
 *   the postMessage protocol constants shared by the frame hook and the view.
 *
 *   The help centre answers HTTP 200 for any path, so a bad slug never errors:
 *   it renders an "article not found" body and reports itself unresolved.
 *   Validate shape here so an injected value can never reach the frame's src.
 *
 **********/

import Articles from "./articles.json";

const Settings = require("settings.json");

const config = Settings[process.env.NODE_ENV]?.help || {};

export const HELP_ORIGIN = config.origin || "";

// an absent origin with embed left on would frame the app inside itself off a
// relative /embed, with the handshake permanently disabled. treat a missing
// origin as the embed being off, so it falls back to the FAQ page instead
export const HELP_EMBED_ENABLED =
  config.embed === true && HELP_ORIGIN.startsWith("https://");

// path prefix the help centre serves its chrome-less embed mode from.
// note: no trailing slash — `/embed/` 307s to `/embed`.
const EMBED_PATH = "/embed";

// mirrors the pattern the help centre enforces on its own side
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_MAX_LENGTH = 80;

export const HELP_PROTOCOL = "identitycheck-help";
export const HELP_PROTOCOL_VERSION = 1;

export const HELP_MESSAGE = {
  HOST_HELLO: "host-hello",
  READY: "ready",
  ROUTE: "route",
  SUPPORT_REQUEST: "support-request",
};

export const HELP_SLUGS = Articles.slugs;

/* returns the slug if it is well-formed, otherwise null */
export function normalizeSlug(slug) {
  if (typeof slug !== "string") return null;
  if (slug.length > SLUG_MAX_LENGTH) return null;
  return SLUG_PATTERN.test(slug) ? slug : null;
}

/* does this route path fall under this map key, on a segment boundary? */
function pathMatches(pathname, key) {
  if (pathname === key) return true;
  return pathname.startsWith(`${key}/`);
}

/* the article that explains this route, or null. longest match wins, so
   /account/integrations/hubspot beats /account/integrations.

   NOTE: test/help-articles.js pins this rule, but has to restate it — this
   module is ESM and resolves settings.json through a webpack alias, so mocha
   can't load it. Change the rule here and change it there too. */
export function slugForPath(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;

  // ignore a trailing slash so /account/users/ matches /account/users
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  let match = null;

  Object.keys(Articles.paths).forEach((key) => {
    if (!pathMatches(path, key)) return;
    if (match && key.length <= match.length) return;
    match = key;
  });

  return match ? Articles.paths[match] : null;
}

/* in-app route for an article */
export function helpPath(slug) {
  const safe = normalizeSlug(slug);
  return safe ? `/help/${safe}` : "/help";
}

/* the help centre URL to frame. an absent or malformed slug falls back to the
   embed home rather than passing the value through */
export function helpEmbedUrl(slug) {
  const safe = normalizeSlug(slug);
  return safe
    ? `${HELP_ORIGIN}${EMBED_PATH}/${safe}`
    : `${HELP_ORIGIN}${EMBED_PATH}`;
}

/* the public (chrome-included) URL, for the open-in-a-new-tab escape hatch */
export function helpPublicUrl(slug) {
  const safe = normalizeSlug(slug);
  return safe ? `${HELP_ORIGIN}/${safe}` : HELP_ORIGIN;
}
